import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Loading from '../../components/ui/Loading';
import api from 'utils/api';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import { toast } from 'react-hot-toast';

dayjs.extend(jalaliday);

// سوالات برای نمایش در مودال (همان سوالات اپلیکیشن)
const questionsMap = {
  self: {
    1: 'رضایت از شرایط زندگی',
    2: 'علاقه به فعالیت‌های قدیمی',
    3: 'روحیه خوب صبحگاهی',
    4: 'احساس پوچی',
    5: 'تمایل به خروج از خانه',
    6: 'فراموشی',
    7: 'احساس انرژی',
    8: 'احساس ارزشمندی',
    9: 'نگرانی از اتفاق بد',
    10: 'مقایسه با دیگران',
    11: 'خرید خانه مستقل',
    12: 'مصرف دارو مستقل',
    13: 'کمک در جابجایی',
    14: 'کمک در لباس پوشیدن',
    15: 'گم کردن وسایل',
  },
  other: {
    1: 'شکایت از شرایط',
    2: 'رها کردن فعالیت‌ها',
    3: 'روحیه خوب',
    4: 'احساس پوچی',
    5: 'عدم تمایل به خروج',
    6: 'ضعف حافظه',
    7: 'احساس انرژی',
    8: 'احساس ارزشمندی',
    9: 'نگرانی مداوم',
    10: 'مایوس بودن',
    11: 'آگاهی به روز هفته',
    12: 'آگاهی به فصل',
    13: 'به یاد آوردن سن/تولد',
    14: 'گم شدن بیرون خانه',
    15: 'گم کردن وسایل',
    16: 'بی‌اختیاری ادرار',
    17: 'کمک در نظافت شخصی',
    18: 'استحمام مستقل',
    19: 'مدیریت داروها',
    20: 'کمک در راه رفتن',
  },
};

const riskAnswerMap = {
  self: {
    1: false, 2: false, 3: false, 4: true, 5: false, 6: true, 7: false,
    8: false, 9: true, 10: true, 11: false, 12: false, 13: true, 14: true, 15: true,
  },
  other: {
    1: true, 2: true, 3: false, 4: true, 5: true, 6: true, 7: false, 8: false,
    9: true, 10: true, 11: false, 12: false, 13: false, 14: true, 15: true,
    16: true, 17: true, 18: false, 19: false, 20: true,
  },
};

const getAssessmentSummary = (assessment) => {
  const totalQuestions = assessment?.testType === 'other' ? 20 : 15;
  const riskCount = Number(assessment?.score);
  if (!Number.isFinite(riskCount)) return null;

  const healthyScore = Math.round(((totalQuestions - riskCount) / totalQuestions) * 20);
  const result = healthyScore >= 16 ? 'normal' : healthyScore >= 10 ? 'warning' : 'danger';
  return { totalQuestions, riskCount, healthyScore, result };
};

const AssessmentManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Modal State
  const [selectedTest, setSelectedTest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNote, setAdminNote] = useState('');

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/assessments');
      setAssessments(res.data);
      setFilteredList(res.data);
    } catch (error) {
      console.error(error);
      toast.error('خطا در دریافت لیست');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    let filtered = assessments;
    if (paymentFilter !== 'all') {
      filtered = filtered.filter((item) => item.paymentStatus === paymentFilter);
    }
    if (riskFilter !== 'all') {
      filtered = filtered.filter((item) => (getAssessmentSummary(item)?.result || item.result || 'unknown') === riskFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => (item.status || 'pending') === statusFilter);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.patientInfo?.name?.toLowerCase().includes(lower) ||
          item.client?.phoneNumber?.includes(lower) ||
          item.client?.name?.toLowerCase().includes(lower),
      );
    }
    setFilteredList(filtered);
    setCurrentPage(1);
  }, [searchTerm, paymentFilter, riskFilter, statusFilter, assessments]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize));
  const paginatedList = filteredList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paymentCounts = assessments.reduce(
    (counts, item) => {
      counts[item.paymentStatus || 'unpaid'] = (counts[item.paymentStatus || 'unpaid'] || 0) + 1;
      return counts;
    },
    {},
  );

  const getPaymentBadge = (status) => {
    const labels = { paid: 'موفق', failed: 'ناموفق', pending: 'در انتظار', held: 'رزرو شده', unpaid: 'پرداخت نشده' };
    const styles = { paid: 'text-green-700 bg-green-50 border-green-200', failed: 'text-red-700 bg-red-50 border-red-200', pending: 'text-yellow-700 bg-yellow-50 border-yellow-200', held: 'text-blue-700 bg-blue-50 border-blue-200', unpaid: 'text-gray-600 bg-gray-50 border-gray-200' };
    return <span className={`px-2 py-1 rounded text-xs border ${styles[status] || styles.unpaid}`}>{labels[status] || 'نامشخص'}</span>;
  };

  const handleOpenModal = (test) => {
    setSelectedTest(test);
    setAdminNote(test.adminNote || '');
    setShowModal(true);
  };

  const handleUpdateStatus = async (status) => {
    try {
      await api.patch(`/api/assessments/${selectedTest._id}/status`, {
        status,
        adminNote,
      });
      toast.success('وضعیت بروزرسانی شد');
      setShowModal(false);
      fetchAssessments();
    } catch (error) {
      toast.error('خطا در آپدیت');
    }
  };

  const getRiskBadge = (assessment) => {
    const summary = getAssessmentSummary(assessment);
    const result = summary?.result || assessment.result;
    const badges = {
      normal: ['سالم', 'bg-green-100 text-green-700'],
      warning: ['در آستانه', 'bg-yellow-100 text-yellow-800'],
      danger: ['حاد / نیازمند پیگیری', 'bg-red-100 text-red-700'],
    };
    const [label, styles] = badges[result] || ['نامشخص', 'bg-gray-100 text-gray-600'];
    return <span className={`${styles} px-2 py-1 rounded text-xs font-bold`}>{label}</span>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded text-xs border border-yellow-200">
            بررسی نشده
          </span>
        );
      case 'contacted':
        return (
          <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-xs border border-blue-200">
            تماس گرفته شد
          </span>
        );
      case 'completed':
        return (
          <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs border border-green-200">
            تکمیل شده
          </span>
        );
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="lg:mr-60 pt-16 p-6">
        <BreadcrumbNavigation />

        <div className="flex justify-between items-center mt-4 mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Icon name="ClipboardList" className="text-primary" />
            درخواست‌های غربالگری آلزایمر
          </h1>
          <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm">
            تعداد کل: {assessments.length}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            ['paid', 'پرداخت موفق', 'text-green-700'],
            ['failed', 'پرداخت ناموفق', 'text-red-700'],
            ['pending', 'در انتظار پرداخت', 'text-yellow-700'],
            ['unpaid', 'پرداخت نشده', 'text-gray-600'],
          ].map(([key, label, color]) => (
            <button key={key} onClick={() => setPaymentFilter(paymentFilter === key ? 'all' : key)} className={`bg-card border border-border rounded-lg p-4 text-right hover:border-primary transition ${paymentFilter === key ? 'ring-2 ring-primary/30' : ''}`}>
              <div className="text-sm text-muted-foreground">{label}</div>
              <div className={`text-2xl font-bold mt-1 ${color}`}>{paymentCounts[key] || 0}</div>
            </button>
          ))}
        </div>

        <div className="bg-card p-4 rounded-lg border border-border mb-6">
          <Input
            placeholder="جستجو (نام بیمار، شماره موبایل)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
          <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="mt-3 border border-border rounded-md px-3 py-2 bg-background text-sm">
            <option value="all">همه پرداخت‌ها</option>
            <option value="paid">موفق</option>
            <option value="pending">در انتظار</option>
            <option value="held">رزرو شده</option>
            <option value="unpaid">پرداخت نشده</option>
          </select>
          <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)} className="mt-3 mr-2 border border-border rounded-md px-3 py-2 bg-background text-sm">
            <option value="all">همه نتایج</option>
            <option value="normal">سالم</option>
            <option value="warning">در آستانه</option>
            <option value="danger">حاد / پرخطر</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mt-3 mr-2 border border-border rounded-md px-3 py-2 bg-background text-sm">
            <option value="all">همه وضعیت‌ها</option>
            <option value="pending">بررسی نشده</option>
            <option value="contacted">تماس گرفته شد</option>
            <option value="completed">تکمیل شده</option>
          </select>
          {(paymentFilter !== 'all' || riskFilter !== 'all' || statusFilter !== 'all' || searchTerm) && (
            <button
              type="button"
              onClick={() => { setSearchTerm(''); setPaymentFilter('all'); setRiskFilter('all'); setStatusFilter('all'); }}
              className="mt-3 mr-2 text-sm text-primary hover:underline">
              حذف فیلترها
            </button>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center">
              <Loading />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border text-sm text-muted-foreground">
                  <tr>
                    <th className="px-6 py-4 text-right">تاریخ</th>
                    <th className="px-6 py-4 text-right">کاربر (شماره)</th>
                    <th className="px-6 py-4 text-right">نام بیمار</th>
                    <th className="px-6 py-4 text-right">نوع تست</th>
                    <th className="px-6 py-4 text-center">امتیاز سلامت</th>
                    <th className="px-6 py-4 text-center">نتیجه</th>
                    <th className="px-6 py-4 text-center">پرداخت</th>
                    <th className="px-6 py-4 text-center">وضعیت</th>
                    <th className="px-6 py-4 text-center">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedList.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {dayjs(item.createdAt)
                          .calendar('jalali')
                          .format('YYYY/MM/DD HH:mm')}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-bold">
                          {item.client?.name || 'ناشناس'}
                        </div>
                        <div className="dir-ltr text-right text-xs text-muted-foreground font-mono">
                          {item.client?.phoneNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-primary">
                        {item.patientInfo?.name || '---'}
                        <span className="text-xs text-gray-400 block">
                          سن: {item.patientInfo?.age}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {item.testType === 'self' ? 'برای خود' : 'برای دیگری'}
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-lg">
                        {getAssessmentSummary(item)?.healthyScore ?? '---'}
                        <span className="block text-[10px] font-normal text-muted-foreground">
                          {getAssessmentSummary(item) ? `${getAssessmentSummary(item).riskCount} پاسخ پرخطر` : 'بدون امتیاز'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getRiskBadge(item)}
                      </td>
                      <td className="px-6 py-4 text-center">{getPaymentBadge(item.paymentStatus)}</td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModal(item)}>
                          بررسی
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paginatedList.length === 0 && (
                    <tr>
                      <td colSpan="9" className="p-8 text-center text-gray-400">
                        هیچ موردی یافت نشد
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && filteredList.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm">
            <span className="text-muted-foreground">
              نمایش {(currentPage - 1) * pageSize + 1} تا {Math.min(currentPage * pageSize, filteredList.length)} از {filteredList.length} مورد
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
                قبلی
              </Button>
              <span className="px-3 py-1 rounded border border-border bg-card">صفحه {currentPage} از {totalPages}</span>
              <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>
                بعدی
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* --- Detail Modal --- */}
      {showModal && selectedTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-modal flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-2xl rounded-xl shadow-xl flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-border flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-lg">جزئیات غربالگری</h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => setShowModal(false)}
              />
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div>
                  <span className="text-gray-500 block">نام بیمار:</span>
                  <span className="font-bold text-lg">
                    {selectedTest.patientInfo?.name || '---'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">سن و جنسیت:</span>
                  <span className="font-bold">
                    {selectedTest.patientInfo?.age} ساله -{' '}
                    {selectedTest.patientInfo?.gender === 'male'
                      ? 'آقا'
                      : 'خانم'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">شماره تماس کاربر:</span>
                  <a
                    href={`tel:${selectedTest.client?.phoneNumber}`}
                    className="font-bold text-blue-600 dir-ltr block text-right hover:underline">
                    {selectedTest.client?.phoneNumber}
                  </a>
                </div>
                <div>
                  <span className="text-gray-500 block">نمره ریسک:</span>
                  <span className="font-bold text-lg text-primary">
                    {getAssessmentSummary(selectedTest)?.healthyScore ?? '---'} از ۲۰
                  </span>
                  <span className="block text-xs text-muted-foreground mt-1">
                    {getAssessmentSummary(selectedTest)
                      ? `${getAssessmentSummary(selectedTest).riskCount} پاسخ پرخطر از ${getAssessmentSummary(selectedTest).totalQuestions}`
                      : 'امتیاز ثبت نشده'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">نتیجه الگوریتم:</span>
                  <span className="font-bold">{getRiskBadge(selectedTest)}</span>
                </div>
              </div>

              {/* Answers */}
              <div>
                <h4 className="font-bold mb-3 border-b pb-2">
                  پاسخ‌های ثبت شده
                </h4>
                <div className="space-y-2">
                  {Object.entries(selectedTest.answers || {}).map(
                    ([qId, answer]) => {
                      const qText =
                        questionsMap[selectedTest.testType]?.[qId] ||
                        `سوال ${qId}`;
                      const isRisk = riskAnswerMap[selectedTest.testType]?.[qId] === Boolean(answer);
                      return (
                        <div
                          key={qId}
                          className="flex justify-between items-center text-sm border-b border-gray-100 pb-1 last:border-0">
                          <span className="text-gray-700">{qText}</span>
                          <span
                            className={`font-bold px-2 rounded ${isRisk ? 'text-red-700 bg-red-50' : 'text-green-700 bg-green-50'}`}>
                            {answer ? 'بله' : 'خیر'}{isRisk ? ' · پرخطر' : ''}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>

              {/* Admin Actions */}
              <div>
                <label className="block text-sm font-bold mb-2">
                  یادداشت ادمین
                </label>
                <textarea
                  className="w-full border border-border rounded-lg p-3 h-24 focus:ring-2 focus:ring-primary outline-none"
                  placeholder="نتیجه تماس یا توضیحات تکمیلی..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                />
              </div>
            </div>

            <div className="p-4 border-t border-border bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                بستن
              </Button>
              {selectedTest.status !== 'completed' && (
                <Button
                  variant="default"
                  onClick={() => handleUpdateStatus('contacted')}>
                  ثبت تماس و یادداشت
                </Button>
              )}
              {selectedTest.status !== 'completed' && (
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleUpdateStatus('completed')}>
                  تکمیل پرونده
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentManagement;
